// -------------------------------------------------------------------
// Types
// -------------------------------------------------------------------
export type AppDownloadDestination =
  | "ios_app_store"
  | "google_play"
  | "android_apk";

export type NordexFooterConfig = {
  /** URL for the trading platform (e.g. https://app.nordex.trade) */
  appUrl: string;
  /** URL for docs (e.g. /docs/ or https://nordex.trade/docs/) */
  docsUrl: string;
  /** URL for support pages (e.g. /support/ or https://nordex.trade/support/) */
  supportUrl: string;
  /** iOS App Store / TestFlight URL */
  iosAppUrl: string;
  /** Google Play URL (empty string = "Coming Soon") */
  googlePlayUrl: string;
  /** Direct APK download URL */
  apkDownloadUrl: string;
  /** Base path for brand assets (e.g. /docs/ or /) */
  basePath: string;
  /** Light logo path relative to basePath (e.g. brand/Nordex_Logo_Black.svg) */
  lightLogo: string;
  /** Dark logo path relative to basePath (e.g. brand/Nordex_Logo_White.svg) */
  darkLogo: string;
  /** Black wordmark SVG path relative to basePath (e.g. brand/Nordex_Logo_Black.svg) */
  wordmarkBlack?: string;
  /** White wordmark SVG path relative to basePath (e.g. brand/Nordex_Logo_White.svg) */
  wordmarkWhite?: string;
  /** Partners page URL (e.g. "/landing/en/partners") */
  partnersUrl?: string;
  /** Investors page URL (e.g. "/landing/en/investors") */
  investorsUrl?: string;
  /**
   * Optional callback when app store links are clicked.
   * For APK links, `next` is provided; call it after tracking to complete navigation.
   * For iOS/Google Play, no `next`; link opens in new tab via default behavior.
   */
  onAppLinkClick?: (
    destination: AppDownloadDestination,
    source: string,
    next?: () => void
  ) => void;
};

// -------------------------------------------------------------------
// NordexFooter
// -------------------------------------------------------------------
// This component uses CSS custom properties for theming so it works
// in both light and dark modes. The consuming app defines:
//
//   --footer-bg, --footer-border, --footer-heading, --footer-text,
//   --footer-text-hover, --footer-muted, --footer-social-bg,
//   --footer-social-border, --footer-social-text
//
// in its own CSS for :root and .dark scopes.
// -------------------------------------------------------------------

export function NordexFooter({ config }: { config: NordexFooterConfig }) {
  const linkClass =
    "text-sm transition-colors" +
    " text-[var(--footer-text)] hover:text-[var(--footer-text-hover)]";
  const headingClass =
    "text-sm font-semibold mb-4 text-[var(--footer-heading)]";

  return (
    <footer
      className="border-t"
      style={{
        background: "var(--footer-bg)",
        borderColor: "var(--footer-border)",
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
        {/* Link columns */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12">
          {/* Product */}
          <div>
            <h4 className={headingClass}>Product</h4>
            <ul className="space-y-3">
              <li>
                <a href={config.appUrl} className={linkClass}>
                  Web Platform
                </a>
              </li>
              <li>
                <a
                  href={config.iosAppUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() =>
                    config.onAppLinkClick?.("ios_app_store", "footer")
                  }
                  className={linkClass}
                >
                  iOS App
                </a>
              </li>
              {config.googlePlayUrl ? (
                <li>
                  <a
                    href={config.googlePlayUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() =>
                      config.onAppLinkClick?.("google_play", "footer")
                    }
                    className={linkClass}
                  >
                    Google Play
                  </a>
                </li>
              ) : (
                <li>
                  <span
                    className="text-sm"
                    style={{ color: "var(--footer-muted)" }}
                  >
                    Google Play{" "}
                    <span className="text-xs">(Coming Soon)</span>
                  </span>
                </li>
              )}
              <li>
                <a
                  href={config.apkDownloadUrl}
                  onClick={(e) => {
                    e.preventDefault();
                    if (config.onAppLinkClick) {
                      config.onAppLinkClick("android_apk", "footer", () => {
                        window.location.href = config.apkDownloadUrl;
                      });
                    } else {
                      window.location.href = config.apkDownloadUrl;
                    }
                  }}
                  className={linkClass}
                >
                  Android APK
                </a>
              </li>
            </ul>
          </div>

          {/* Support */}
          <div>
            <h4 className={headingClass}>Support</h4>
            <ul className="space-y-3">
              <li>
                <a href={`${config.supportUrl}contact`} className={linkClass}>
                  Contact Us
                </a>
              </li>
              <li>
                <a href={`${config.supportUrl}feedback`} className={linkClass}>
                  Feedback
                </a>
              </li>
              <li>
                <a
                  href={`${config.supportUrl}api-access`}
                  className={linkClass}
                >
                  API
                </a>
              </li>
              <li>
                <a href={config.docsUrl} className={linkClass}>
                  Documentation
                </a>
              </li>
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className={headingClass}>Company</h4>
            <ul className="space-y-3">
              <li>
                <a href={`${config.supportUrl}about`} className={linkClass}>
                  About
                </a>
              </li>
              <li>
                <a href={`${config.supportUrl}careers`} className={linkClass}>
                  Careers
                </a>
              </li>
              {config.partnersUrl && (
                <li>
                  <a href={config.partnersUrl} className={linkClass}>
                    Partners
                  </a>
                </li>
              )}
              {config.investorsUrl && (
                <li>
                  <a href={config.investorsUrl} className={linkClass}>
                    Investors
                  </a>
                </li>
              )}
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h4 className={headingClass}>Resources</h4>
            <ul className="space-y-3">
              <li>
                <a
                  href={`${config.supportUrl}media-kit`}
                  className={linkClass}
                >
                  Media Kit
                </a>
              </li>
              <li>
                <a
                  href="https://status.nordex.trade"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={linkClass}
                >
                  Status
                </a>
              </li>
              <li>
                <a href={`${config.supportUrl}terms`} className={linkClass}>
                  Terms of Use
                </a>
              </li>
              <li>
                <a href={`${config.supportUrl}privacy`} className={linkClass}>
                  Privacy Policy
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div
          className="pt-8 border-t"
          style={{ borderColor: "var(--footer-border)" }}
        >
          <div className="flex flex-col items-center gap-6 md:flex-row md:justify-between md:items-center">
            {/* Logo + tagline */}
            <div className="flex flex-col items-center md:items-start">
              <img
                src={`${config.basePath}${config.lightLogo}`}
                alt="Nordex"
                className="h-6 footer-logo-light"
              />
              <img
                src={`${config.basePath}${config.darkLogo}`}
                alt="Nordex"
                className="h-6 footer-logo-dark"
              />
              <p
                className="text-xs mt-2 max-w-[250px] text-center md:text-left"
                style={{ color: "var(--footer-muted)" }}
              >
                Trade crypto perpetuals with confidence. Non-custodial and
                secure by design.
              </p>
            </div>

            {/* Copyright */}
            <p className="text-sm order-last md:order-none" style={{ color: "var(--footer-text)" }}>
              &copy; {new Date().getFullYear()} Nordex. All rights reserved.
            </p>

            {/* Social links */}
            <div className="flex items-center gap-4">
              {[
                {
                  href: "https://x.com/nordex",
                  title: "Follow us on X (Twitter)",
                  label: "\uD835\uDD4F",
                },
                {
                  href: "https://discord.gg/nordex",
                  title: "Join our Discord",
                  label: "D",
                },
                {
                  href: "https://t.me/nordex",
                  title: "Join our Telegram",
                  label: "T",
                },
              ].map((social) => (
                <a
                  key={social.href}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={social.title}
                  className="w-10 h-10 rounded-full flex items-center justify-center transition-colors"
                  style={{
                    background: "var(--footer-social-bg)",
                    border: "1px solid var(--footer-social-border)",
                    color: "var(--footer-social-text)",
                  }}
                >
                  {social.label}
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}

// -------------------------------------------------------------------
// NordexBrandFooter — large watermark logo below the main footer
// -------------------------------------------------------------------
// Uses CSS variables: --footer-bg for background.
// Uses CSS classes: footer-brand-light / footer-brand-dark to toggle
// between the black and white wordmark SVGs based on theme.
// -------------------------------------------------------------------

export function NordexBrandFooter({
  config,
}: {
  config: Pick<NordexFooterConfig, "basePath" | "wordmarkBlack" | "wordmarkWhite">;
}) {
  const blackSrc = `${config.basePath}${config.wordmarkBlack || "brand/Nordex_Logo_Black.svg"}`;
  const whiteSrc = `${config.basePath}${config.wordmarkWhite || "brand/Nordex_Logo_White.svg"}`;

  const maskGradient = {
    mobile: "linear-gradient(to bottom, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.5) 50%, rgba(0,0,0,0.15) 85%, rgba(0,0,0,0) 100%)",
    desktop: "linear-gradient(to bottom, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.35) 40%, rgba(0,0,0,0.1) 80%, rgba(0,0,0,0) 100%)",
  };

  return (
    <div
      className="relative w-full overflow-hidden pt-16 pb-8"
      style={{ background: "var(--footer-bg)" }}
    >
      <div className="relative flex justify-center items-center">
        <div className="relative w-full max-w-[1400px] px-4">
          {/* Mobile */}
          <div
            className="relative md:hidden"
            style={{
              maskImage: maskGradient.mobile,
              WebkitMaskImage: maskGradient.mobile,
            }}
          >
            <img
              src={blackSrc}
              alt="Nordex"
              loading="lazy"
              className="w-full h-auto opacity-20 footer-brand-light"
            />
            <img
              src={whiteSrc}
              alt="Nordex"
              loading="lazy"
              className="w-full h-auto opacity-10 footer-brand-dark"
            />
          </div>
          {/* Desktop */}
          <div
            className="relative hidden md:block"
            style={{
              maskImage: maskGradient.desktop,
              WebkitMaskImage: maskGradient.desktop,
            }}
          >
            <img
              src={blackSrc}
              alt="Nordex"
              loading="lazy"
              className="w-full h-auto opacity-15 footer-brand-light"
            />
            <img
              src={whiteSrc}
              alt="Nordex"
              loading="lazy"
              className="w-full h-auto opacity-8 footer-brand-dark"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
